import { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import { api } from "../../../env";
import Input, { useInput } from "../../../components/input";
import Loading from "../../../components/loading";
import { ChartState, getDefaultDates, toISODate } from "./charts";
import EnrollmentDrilldown from "./EnrollmentDrilldown";

export default function EnrollmentsChart({ programId }) {
    const { today, defaultStart } = getDefaultDates()
    const startInput = useInput("Start Date", "calendar_today", "date", [], toISODate(defaultStart));
    const endInput = useInput("End Date", "event", "date", [], toISODate(today));
    const [categories, setCategories] = useState([]);
    const [mentorsData, setMentorsData] = useState([]);
    const [menteesData, setMenteesData] = useState([]);
    const [chartState, setChartState] = useState(ChartState.LOADING);
    const [selectedMonth, setSelectedMonth] = useState(null);

    // Apex keeps the event handlers from the options it was created with,
    // so read categories through a ref to always get the current ones
    const categoriesRef = useRef(categories);
    categoriesRef.current = categories;

    useEffect(() => {
        setSelectedMonth(null);
        if (!programId) return;
        // A cleared date would leave the old chart showing, and its drilldown would query a different range
        if (!startInput._value || !endInput._value) {
            setChartState(ChartState.INCOMPLETE_RANGE);
            return;
        }
        setChartState(ChartState.LOADING);
        const controller = new AbortController()
        api.get(`programs/${programId}/timeseries/enrollments`, {
            signal: controller.signal,
            params: { startDate: startInput._value, endDate: endInput._value }
        }).then(res => {
            const data = res.data.data;
            setCategories(data.map(d => d.month));
            setMentorsData(data.map(d => d.totalMentors || 0.05));
            setMenteesData(data.map(d => d.totalMentees || 0.05));
            setChartState(data.length === 0 ? ChartState.NO_DATA : ChartState.SHOWING);
        }).catch(() => {
            // Aborted by a newer effect run, which owns the chart state now
            if (!controller.signal.aborted) setChartState(ChartState.ERROR);
        });
        return () => controller.abort()
    }, [programId, startInput._value, endInput._value]);

    const selectMonth = (index) => {
        const month = categoriesRef.current[index];
        if (month) setSelectedMonth(month);
    };

    const style = getComputedStyle(document.documentElement);
    const accentColor = `rgb(${style.getPropertyValue('--accent').trim()})`;
    const primaryColor = `rgb(${style.getPropertyValue('--primary-light').trim()})`;

    const options = {
        chart: {
            type: 'bar',
            height: 350,
            stacked: true,
            events: {
                // Fires for either stacked segment, dataPointIndex is the month either way
                dataPointSelection: (event, chartContext, { dataPointIndex }) => selectMonth(dataPointIndex),
                // Month labels are clickable too, since months with few enrollments have tiny bars
                xAxisLabelClick: (event, chartContext, { labelIndex }) => selectMonth(labelIndex),
            },
        },
        // Don't keep the clicked bar highlighted, the drilldown panel shows which month is selected
        states: { active: { filter: { type: 'none' } } },
        colors: [accentColor, primaryColor],
        plotOptions: { bar: { horizontal: false, columnWidth: '25%', borderRadius: 5, borderRadiusApplication: 'end' } },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 2, colors: ['transparent'] },
        xaxis: { categories },
        yaxis: { title: { text: 'Enrollments' } },
        fill: { opacity: 1 },
        legend: { position: 'top' },
        tooltip: { y: { formatter: (val) => `${Math.round(val)}` } }
    };

    return (
        <div>
            <div className="flex gap-s v-spacer-xs">
                <Input state={startInput} />
                <Input state={endInput} />
            </div>
            <div className="clickable-chart">
                {chartState === ChartState.LOADING && <Loading />}
                {chartState === ChartState.ERROR && <div className="error-card standard-padding"><p className="card-title error">Error fetching data</p></div>}
                {chartState === ChartState.NO_DATA && <div className="dashed-card standard-padding"><p className="card-title">No Data</p></div>}
                {chartState === ChartState.INCOMPLETE_RANGE && <div className="dashed-card standard-padding"><p className="card-title">Select a start and end date</p></div>}
                {chartState === ChartState.SHOWING && <ReactApexChart
                    options={options}
                    series={[
                        { name: 'Mentors', data: mentorsData },
                        { name: 'Mentees', data: menteesData }
                    ]}
                    type="bar"
                    height={350}
                />}
            </div>
            {chartState === ChartState.SHOWING && selectedMonth && (
                <EnrollmentDrilldown
                    key={selectedMonth}
                    programId={programId}
                    month={selectedMonth}
                    startDate={startInput._value}
                    endDate={endInput._value}
                    onClose={() => setSelectedMonth(null)}
                />
            )}
        </div>
    );
}